import { format } from 'date-fns';
import csvDownload from 'json-to-csv-export';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  applyCapitalGainsTax,
  calculateIssuerTaxes,
  checkCapitalGainsTaxType,
  checkIfCapitalGainsTaxesApply,
} from 'src/components/payment/rent/TaxSystem';
import { Payout, SPV } from 'src/models';
import { PayoutStatus } from 'src/models/enum';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { Chip, IconButton, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Grid from '@mui/material/Grid';
import { TCompleteAssetSchema } from '@brickwise/asset-validation/dist/schema/completeAsset';

type TableRow = {
  createdAt: string;
  id: Payout['id'];
  payoutInformation: Payout['payoutInformation'];
  assetId: Payout['assetId'];
  payoutAmount: { $numberDecimal: Payout['payoutAmount'] };
  executionDate: string;
  status: Payout['status'];
  transactions?: Payout['transactions'];
};

interface Props {
  removeInitPayout: (payoutId: string) => void;
  payouts?: any;
  spv: SPV;
  asset: TCompleteAssetSchema;
}

export const PayoutTransactions: FC<Props> = ({ removeInitPayout, payouts, spv, asset }) => {
  const { t } = useTranslation();

  const renderChip = (payoutStatus: PayoutStatus) => {
    switch (payoutStatus) {
      case PayoutStatus.init:
        return <Chip label={t('payment.account.table.chip.init')} />;
      case PayoutStatus.pending:
        return (
          <Chip label={t('payment.account.table.chip.pending')} sx={{ color: 'rgb(114 59 19)', backgroundColor: 'rgb(253 246 178)' }} />
        );
      case PayoutStatus.queued:
        return (
          <Chip label={t('payment.account.table.chip.queued')} sx={{ color: 'rgb(114 59 19)', backgroundColor: 'rgb(253 246 178)' }} />
        );
      case PayoutStatus.transferred:
        return (
          <Chip label={t('payment.account.table.chip.transferred')} sx={{ color: 'rgb(3 84 63)', backgroundColor: 'rgb(222 247 236)' }} />
        );
      case PayoutStatus.failed:
        return (
          <Chip label={t('payment.account.table.chip.failed')} sx={{ color: 'rgb(155 28 28)', backgroundColor: 'rgb(253 232 232)' }} />
        );
      case PayoutStatus.rejected:
        return (
          <Chip label={t('payment.account.table.chip.declined')} sx={{ color: 'rgb(155 28 28)', backgroundColor: 'rgb(253 232 232)' }} />
        );
      default:
        return <Chip label={t('payment.account.table.chip.error')} sx={{ color: 'rgb(155 28 28)', backgroundColor: 'rgb(253 232 232)' }} />;
    }
  };

  const renderTrashIcon = (payoutStatus: PayoutStatus, payoutId: string) => {
    switch (payoutStatus) {
      case PayoutStatus.init:
        return (
          <IconButton aria-label="delete" style={{ verticalAlign: 'middle' }} onClick={() => removeInitPayout(payoutId)}>
            <DeleteForeverIcon />
          </IconButton>
        );
      default:
        return null;
    }
  };

  const calculateIssuerTax = (totalRent: string) => {
    const totalRentFloat = Number(totalRent);
    const taxType = checkCapitalGainsTaxType(spv);
    const [, taxTypeArray] = applyCapitalGainsTax(taxType, totalRentFloat);
    const issuerTaxes = taxTypeArray.reduce((acc, tax) => acc + (calculateIssuerTaxes(tax, totalRentFloat, false) as number), 0);
    return issuerTaxes;
  };

  const renderTaxes = (totalRent: string) => {
    // Check taxes, if Asset is not taxable return 0
    const doCapitalTaxesApply = checkIfCapitalGainsTaxesApply(asset.investmentDetails.securityType);
    if (!doCapitalTaxesApply) return 0;
    const issuerTaxes = calculateIssuerTax(totalRent);
    return issuerTaxes.toFixed(2);
  };

  const renderDownloadBUtton = (payout: TableRow) => {
    // return an array of objects with taxes from the transactions array
    const doCapitalTaxesApply = checkIfCapitalGainsTaxesApply(asset.investmentDetails.securityType);
    if (!doCapitalTaxesApply) return <span></span>;
    if (payout.status !== PayoutStatus.transferred) return <span></span>;
    if (!payout || !payout.transactions) return <span></span>;

    const taxHeaders = new Set<string>();
    const taxes = payout.transactions.map((transaction) => {
      if (!transaction.taxes) return {};
      // Enrich the tax object with the userId and the amount of shares and payout gross and net
      const taxWithUserId = Object.assign({
        userId: transaction.userId,
        shares: transaction.shares,
        gross: transaction?.amountBeforeTax?.$numberDecimal ?? transaction.amount?.$numberDecimal,
        net: transaction.amount?.$numberDecimal,
      });
      transaction.taxes.forEach((tax) => {
        taxWithUserId[tax.type + 'percentage'] = tax.percentage;
        taxWithUserId[tax.type + 'amount'] = tax.amount;
        taxHeaders.add(tax.type + ' ' + t('payment.account.table.csv.percentage'));
        taxHeaders.add(tax.type + ' ' + t('payment.account.table.csv.taxAmount'));
      });
      // Remove the MongoDB _id field from the object
      delete taxWithUserId._id;
      return taxWithUserId;
    });
    const headers = [
      t('payment.account.table.csv.user'),
      t('payment.account.table.csv.AmountOfShares'),
      t('payment.account.table.csv.gross'),
      t('payment.account.table.csv.net'),
      ...[...taxHeaders],
    ]; // headers: ['User', 'Amount of shares', 'Gross', 'Net', and then dynamic '${Tax Type} Percentage', '${Tax Type} Tax Amount'],
    const dataToConvert = {
      data: taxes,
      filename: 'investor_tax_report',
      delimiter: ';',
      headers: headers,
    };
    return (
      <IconButton aria-label="download" style={{ verticalAlign: 'middle' }} onClick={() => csvDownload(dataToConvert)}>
        <FileDownloadIcon />
      </IconButton>
    );
  };

  const renderNetRent = (totalRent: string) => {
    const doCapitalTaxesApply = checkIfCapitalGainsTaxesApply(asset.investmentDetails.securityType);
    if (!doCapitalTaxesApply) return totalRent;
    const issuerTaxes = calculateIssuerTax(totalRent);
    const netRent = Number(totalRent) - issuerTaxes;
    return netRent.toFixed(2);
  };

  const renderDate = (date: string) => {
    return format(new Date(date ?? new Date()), 'dd/MM/yyyy');
  };
  return (
    <Paper>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>{t('payment.account.table.creation')}</TableCell>
              <TableCell>{t('payment.account.table.transactionId')}</TableCell>
              <TableCell>{t('asset.payout.table.subject')}</TableCell>
              <TableCell>{t('asset.payout.table.asset')}</TableCell>
              <TableCell>{t('payment.account.table.amount')}</TableCell>
              <TableCell>{t('payment.account.table.taxes')}</TableCell>
              <TableCell>{t('payment.account.table.net')}</TableCell>
              <TableCell>{t('asset.payout.table.executionDate')}</TableCell>
              <TableCell align="right">{t('payment.account.table.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payouts &&
              payouts.map((row: TableRow) => (
                <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    {renderDate(row.createdAt)}
                  </TableCell>
                  <TableCell align="left">{row.id}</TableCell>
                  <TableCell align="left">{row.payoutInformation}</TableCell>
                  <TableCell align="left">{row.assetId}</TableCell>
                  <TableCell align="left">
                    <>€ {row.payoutAmount.$numberDecimal}</>
                  </TableCell>
                  <TableCell align="left">
                    <>
                      € {renderTaxes(row.payoutAmount.$numberDecimal as unknown as string)} {renderDownloadBUtton(row)}
                    </>
                  </TableCell>
                  <TableCell align="left">€ {renderNetRent(row.payoutAmount.$numberDecimal as unknown as string)}</TableCell>
                  <TableCell align="left">{renderDate(row.executionDate)}</TableCell>
                  <TableCell align="right">
                    {renderChip(row.status)}
                    {renderTrashIcon(row.status, row.id)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {payouts.length === 0 && (
        <Grid container flexDirection="column" textAlign="center" sx={{ p: 10 }}>
          <Typography color="primary">
            <MoneyOffIcon sx={{ fontSize: 100 }} color="primary" />
          </Typography>
          <Typography color="primary">{t('asset.payout.table.noTransactions')}</Typography>
        </Grid>
      )}
    </Paper>
  );
};
